import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Plan, PaymentRequestData, PaymentResponseData } from '../types/plan';
import { SignUpRequest } from '../types';
import PlanSelectionPage from './PlanSelectionPage';
import PaymentPage from './PaymentPage';
import SignUpForm from './SignUpForm';

type FlowStep = 'signup' | 'plan-selection' | 'payment';

interface SignUpFlowPageProps {
    onBack: () => void;
    initialFormData?: SignUpRequest;
}

const SignUpFlowPage: React.FC<SignUpFlowPageProps> = ({ onBack, initialFormData }) => {
    const [currentStep, setCurrentStep] = useState<FlowStep>('signup');
    const [signUpData, setSignUpData] = useState<SignUpRequest | null>(initialFormData || null);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [paymentResponse, setPaymentResponse] = useState<PaymentResponseData | null>(null);
    const [closePaymentModal, setClosePaymentModal] = useState<(() => void) | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { signUp, refreshCurrentOrganization } = useAuth();

    const handleSignUpComplete = async (data: SignUpRequest) => {
        setSignUpData(data);
        setLoading(true);
        
        try {
            // Criar conta primeiro (sem plano)
            const result = await signUp({
                ...data,
                planId: undefined // Não enviar planId para criar conta sem plano
            });

            if (result.success) {
                // Após criar conta, ir para seleção de plano
                setError(null); // Limpar erro se sucesso
                setCurrentStep('plan-selection');
            } else {
                throw new Error(result.error || 'Erro ao criar conta');
            }
        } catch (error: any) {
            console.error('Error creating account:', error);
            // Mostrar erro ao usuário
            setError(error.message || 'Erro ao criar conta. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handlePlanSelect = (plan: Plan) => {
        setSelectedPlan(plan);
        setCurrentStep('payment');
    };

    const handlePaymentSuccess = async (response: PaymentResponseData) => {
        setPaymentResponse(response);

        // Após pagamento bem-sucedido, redirecionar para admin
        // O usuário já está autenticado (conta foi criada antes)
        if (response.status === 'Success') {
            // Atualizar organização para refletir o plano selecionado
            // Remover flag de necessidade de seleção de plano
            localStorage.removeItem('needsPlanSelection');
            
            // Atualizar organização no contexto
            try {
                await refreshCurrentOrganization();
            } catch (error) {
                console.error('Error refreshing organization:', error);
            }
            
            // Aguardar um pouco para a modal mostrar a mensagem de sucesso
            // Depois fechar modal e redirecionar
            setTimeout(() => {
                // Fechar modal de pagamento
                if (closePaymentModal) {
                    closePaymentModal();
                }
                
                // Redirecionar diretamente para admin
                navigate('/admin', { state: { showWelcome: true } });
            }, 2000);
        }
    };

    const handleBack = () => {
        switch (currentStep) {
            case 'plan-selection':
                setCurrentStep('signup');
                break;
            case 'payment':
                setCurrentStep('plan-selection');
                break;
            default:
                onBack();
        }
    };

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 'signup':
                return (
                    <SignUpForm
                        onSignUpComplete={handleSignUpComplete}
                        onBack={onBack}
                        initialData={signUpData || undefined}
                        isLoading={loading}
                    />
                );

            case 'plan-selection':
                return (
                    <PlanSelectionPage
                        onPlanSelect={handlePlanSelect}
                        onBack={handleBack}
                        signUpData={signUpData}
                    />
                );

            case 'payment':
                return selectedPlan && signUpData ? (
                    <PaymentPage
                        plan={selectedPlan}
                        customerData={{
                            name: signUpData.name,
                            email: signUpData.email,
                            phone: signUpData.phone
                        }}
                        onPaymentSuccess={handlePaymentSuccess}
                        onBack={handleBack}
                        onCloseModal={setClosePaymentModal}
                    />
                ) : null;

            default:
                return null;
        }
    };

    return (
        <div>
            {error && (
                <div style={{ 
                    position: 'fixed', 
                    top: 20, 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    zIndex: 9999,
                    padding: '12px 24px',
                    backgroundColor: '#ff4444',
                    color: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                    {error}
                </div>
            )}
            {renderCurrentStep()}
        </div>
    );
};

export default SignUpFlowPage;
